import { FormattedMessage } from 'react-intl';

import { Separator } from '@/components/ui/separator';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { cn } from '@/utils/classnames';

import { IneligibleOpenings } from './ineligible-openings';
import { OpeningRow } from './opening-row';

interface OpeningsPanelProps {
  className?: string;
}

/**
 * Right-side panel that lists the openings the user is posting to, lets them remove individual openings
 */
export function OpeningsPanel({ className }: OpeningsPanelProps) {
  const { formatCurrency } = useCurrencySymbol();
  const { selectedOpeningIds, totalBudget } = useJobPostingContext();

  return (
    <aside
      className={cn(
        'border-fx-neutral-200 mb-6 flex w-[384px] shrink-0 flex-col overflow-hidden rounded-lg border bg-white shadow-sm',
        className,
      )}
    >
      {/* Header */}
      <header className="border-fx-neutral-200 bg-fx-neutral-100 border-b p-3">
        <Typography variant="subtitle2" className="text-fx-neutral-900">
          <FormattedMessage
            defaultMessage="Selected Openings ({count})"
            description="Header showing the count of openings in the job-posting flow"
            values={{ count: selectedOpeningIds.length }}
          />
        </Typography>
      </header>

      <div className="flex-1 overflow-y-auto">
        {/* Openings selected for posting */}
        <ul className="divide-fx-neutral-200 divide-y">
          {selectedOpeningIds.map(openingId => (
            <OpeningRow key={openingId} openingId={openingId} />
          ))}
        </ul>

        <Separator />

        {/* Ineligible openings - openings that are either inactive or sourcing-disabled */}
        <IneligibleOpenings />
      </div>

      <div className="mt-auto flex items-center justify-between p-4">
        <Typography variant="body1" className="text-fx-neutral-600">
          <FormattedMessage
            defaultMessage="Total Spend"
            description="Total budget of all openings"
          />
        </Typography>
        <Typography variant="h3">{formatCurrency(totalBudget)}</Typography>
      </div>
    </aside>
  );
}
